import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FaCar, FaShuttleVan, FaMotorcycle, FaPhoneAlt, FaWhatsapp } from 'react-icons/fa';
import { GiTireIron } from 'react-icons/gi';
import { useTranslation } from 'react-i18next';
import vehicleHero from '../assets/Mawathage photos/vh.png';
import carImage from '../assets/Mawathage photos/images.jpeg';
import suvImage from '../assets/Mawathage photos/CR-Cars-InlineHero-2026-Toyota-Grand-Highlander-Limited-f-driving-2-26.jpeg';
import vanImage from '../assets/Mawathage photos/33191514_455297244929816_830699468784402432_n.jpg';
import threeWheelerImage from '../assets/Mawathage photos/Three-wheeler.jpg';
import bikeImage from '../assets/Mawathage photos/images (1).jpeg';

const rentalImages = [carImage, suvImage, vanImage, threeWheelerImage, bikeImage];
const rentalIcons = [FaCar, FaCar, FaShuttleVan, GiTireIron, FaMotorcycle];

const VehicleRental = () => {
    const { t } = useTranslation('vehicleRental');
    const [formData, setFormData] = useState({ name: '', phone: '', email: '', remarks: '' });
    const [quantities, setQuantities] = useState({});
    const [isSent, setIsSent] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const vehicles = ['cars', 'suvs', 'vans', 'threeWheelers', 'bikes'].map((key, index) => ({
        key,
        title: t(`${key}.title`),
        description: t(`${key}.description`),
        features: t(`${key}.features`, { returnObjects: true }),
        image: rentalImages[index],
        Icon: rentalIcons[index],
    }));

    const updateField = (event) => {
        const { name, value } = event.target;
        setFormData((current) => ({ ...current, [name]: value }));
        setIsSent(false);
    };

    const toggleVehicle = (key) => {
        setQuantities((current) => {
            if (current[key]) {
                const next = { ...current };
                delete next[key];
                return next;
            }
            return { ...current, [key]: 1 };
        });
        setIsSent(false);
    };

    const updateQuantity = (key, value) => {
        const quantity = Math.max(1, Number(value) || 1);
        setQuantities((current) => ({ ...current, [key]: quantity }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const selectedVehicles = vehicles
            .filter(({ key }) => quantities[key])
            .map(({ key, title }) => `- ${title}: ${quantities[key]}`)
            .join('\n');

        if (!selectedVehicles) return;

        const message = [
            t('whatsappSubject', { defaultValue: 'New vehicle rental inquiry' }),
            '',
            `${t('nameLabel', { defaultValue: 'Name' })}: ${formData.name}`,
            `${t('phoneLabel', { defaultValue: 'Telephone' })}: ${formData.phone}`,
            `${t('emailLabel', { defaultValue: 'Email' })}: ${formData.email || '-'}`,
            '',
            `${t('vehiclesLabel', { defaultValue: 'Vehicles requested' })}:\n${selectedVehicles}`,
            '',
            `${t('remarksLabel', { defaultValue: 'Remarks' })}:\n${formData.remarks || '-'}`,
        ].join('\n');

        window.open(`https://wa.me/94742742794?text=${encodeURIComponent(message)}`, '_blank');
        setIsSent(true);
    };

    return (
        <div className="min-h-screen bg-white text-[#173F2D]">
            <header className="relative flex min-h-[460px] items-center justify-center overflow-hidden border-b border-[#C9A227]/40 bg-[#F7FAF8] sm:min-h-[620px]">
                <img src={vehicleHero} alt={t('heroAlt')} className="absolute inset-0 h-full w-full object-cover" loading="eager" decoding="async" fetchPriority="high" />
                <div className="absolute inset-0 bg-[#173F2D]/65" />
                <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="relative z-10 mx-auto w-full max-w-5xl px-5 py-20 text-center sm:px-8 md:py-28 will-change-transform">
                    <p className="mb-5 text-sm font-semibold uppercase tracking-[0.3em] text-[#F2D36B]">{t('heroEyebrow')}</p>
                    <h1 className="text-4xl font-serif font-light tracking-tight text-white sm:text-5xl md:text-7xl">{t('heroTitle')}</h1>
                    <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-white/90 sm:text-xl">{t('heroSubtitle')}</p>
                </motion.div>
            </header>

            <main className="mx-auto max-w-7xl px-5 py-14 sm:px-8 md:py-20 lg:px-12">
                <div className="mx-auto mb-12 max-w-3xl text-center md:mb-16">
                    <h2 className="text-3xl font-serif font-light text-[#173F2D] sm:text-4xl">{t('sectionTitle')}</h2>
                    <p className="mt-4 text-lg leading-relaxed text-[#46705A]">{t('sectionDescription')}</p>
                </div>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {vehicles.map(({ key, title, description, features, image, Icon }, index) => (
                        <motion.article key={key} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ delay: index * 0.04, duration: 0.35 }} className="group overflow-hidden rounded-xl border border-[#DCE9E1] bg-white shadow-[0_10px_30px_rgba(23,63,45,0.08)] transition-all duration-300 hover:-translate-y-1 hover:border-[#C9A227]/70 hover:shadow-xl will-change-transform">
                            <div className="relative aspect-[16/10] overflow-hidden"><img src={image} alt={title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading={index < 2 ? 'eager' : 'lazy'} decoding="async" fetchPriority={index < 2 ? 'high' : 'auto'} /><div className="absolute inset-0 bg-gradient-to-t from-[#173F2D]/75 via-transparent to-transparent" /><div className="absolute bottom-4 left-4 flex h-12 w-12 items-center justify-center rounded-lg bg-[#C9A227] text-xl text-white"><Icon /></div></div>
                            <div className="p-6"><h3 className="text-2xl font-serif font-light text-[#173F2D]">{title}</h3><p className="mt-3 min-h-[4.5rem] text-base leading-relaxed text-[#46705A]">{description}</p><ul className="mt-5 space-y-2 text-sm text-[#587364]">{Array.isArray(features) && features.map((feature) => <li key={feature} className="flex gap-2"><span className="text-[#C9A227]">+</span>{feature}</li>)}</ul><a href="tel:+94742742794" className="mt-6 inline-flex items-center gap-2 font-semibold text-[#1F5D42] transition hover:text-[#B18A16]"><FaPhoneAlt aria-hidden="true" />{t('enquire')}</a></div>
                        </motion.article>
                    ))}
                </div>

                <motion.section
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    className="mx-auto mt-16 max-w-5xl border-t border-[#DCE9E1] pt-14 md:mt-24 md:pt-20 will-change-transform"
                    style={{ contentVisibility: 'auto' }}
                >
                    <div className="mb-10 max-w-2xl">
                        <p className="mb-3 text-sm font-semibold uppercase tracking-[0.25em] text-[#B18A16]">{t('inquiryEyebrow', { defaultValue: 'Vehicle inquiries' })}</p>
                        <h2 className="text-3xl font-serif font-light text-[#173F2D] sm:text-4xl">{t('inquiryTitle', { defaultValue: 'Tell us what you need' })}</h2>
                        <p className="mt-4 text-lg leading-relaxed text-[#46705A]">{t('inquiryDescription', { defaultValue: 'Choose one or more vehicle types and send your request directly to our WhatsApp team.' })}</p>
                    </div>

                    <form onSubmit={handleSubmit} className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr]">
                        <div>
                            <fieldset>
                                <legend className="mb-4 text-lg font-semibold text-[#173F2D]">{t('vehiclesLabel', { defaultValue: 'Select vehicles and quantity' })}</legend>
                                <div className="grid gap-3 sm:grid-cols-2">
                                    {vehicles.map(({ key, title, Icon }) => (
                                        <label key={key} className="flex min-h-[74px] cursor-pointer items-center gap-3 rounded-lg border border-[#DCE9E1] bg-[#F7FAF8] px-4 py-3 transition hover:border-[#C9A227]">
                                            <input type="checkbox" checked={Boolean(quantities[key])} onChange={() => toggleVehicle(key)} className="h-5 w-5 accent-[#1F5D42]" />
                                            <Icon className="shrink-0 text-lg text-[#C9A227]" aria-hidden="true" />
                                            <span className="min-w-0 flex-1 font-medium text-[#173F2D]">{title}</span>
                                            {quantities[key] && <input aria-label={`${title} quantity`} type="number" min="1" value={quantities[key]} onClick={(event) => event.stopPropagation()} onChange={(event) => updateQuantity(key, event.target.value)} className="w-16 rounded border border-[#C9A227] bg-white px-2 py-1 text-center text-[#173F2D]" />}
                                        </label>
                                    ))}
                                </div>
                            </fieldset>
                            <label className="mt-6 block text-sm font-semibold text-[#173F2D]">
                                {t('remarksLabel', { defaultValue: 'Remarks' })}
                                <textarea name="remarks" value={formData.remarks} onChange={updateField} rows="5" placeholder={t('remarksPlaceholder', { defaultValue: 'Dates, pickup details, or anything else we should know' })} className="mt-2 block w-full resize-y rounded-lg border border-[#DCE9E1] px-4 py-3 font-normal text-[#173F2D] outline-none transition placeholder:text-[#789080] focus:border-[#C9A227]" />
                            </label>
                        </div>

                        <div className="rounded-xl bg-[#173F2D] p-6 text-white shadow-[0_10px_30px_rgba(23,63,45,0.12)] sm:p-8">
                            <div className="grid gap-5">
                                <label className="text-sm font-semibold">{t('nameLabel', { defaultValue: 'Name' })} *<input required name="name" value={formData.name} onChange={updateField} type="text" autoComplete="name" className="mt-2 block w-full rounded-lg border-0 px-4 py-3 font-normal text-[#173F2D] outline-none focus:ring-2 focus:ring-[#F2D36B]" /></label>
                                <label className="text-sm font-semibold">{t('phoneLabel', { defaultValue: 'Telephone' })} *<input required name="phone" value={formData.phone} onChange={updateField} type="tel" autoComplete="tel" className="mt-2 block w-full rounded-lg border-0 px-4 py-3 font-normal text-[#173F2D] outline-none focus:ring-2 focus:ring-[#F2D36B]" /></label>
                                <label className="text-sm font-semibold">{t('emailLabel', { defaultValue: 'Email (optional)' })}<input name="email" value={formData.email} onChange={updateField} type="email" autoComplete="email" className="mt-2 block w-full rounded-lg border-0 px-4 py-3 font-normal text-[#173F2D] outline-none focus:ring-2 focus:ring-[#F2D36B]" /></label>
                            </div>
                            <button type="submit" disabled={!Object.keys(quantities).length} className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-lg bg-[#C9A227] px-5 py-3.5 font-semibold text-white transition hover:bg-[#B18A16] disabled:cursor-not-allowed disabled:opacity-50"><FaWhatsapp aria-hidden="true" />{t('sendInquiry', { defaultValue: 'Send inquiry on WhatsApp' })}</button>
                            {isSent && <p role="status" className="mt-4 text-sm text-[#F2D36B]">{t('inquirySent', { defaultValue: 'WhatsApp has opened with your inquiry.' })}</p>}
                        </div>
                    </form>
                </motion.section>
            </main>
        </div>
    );
};

export default VehicleRental;
